import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import { Box, Button, Grid } from "@mui/material";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../components/CustomSelect";
import CustomSelectScroll from "../../../../../components/CustomSelectScroll";
import Input from "../../../../../components/Input";
import ReportViewer from "../../../../../components/ReportViewer";
import SuperBillReport from "../../../../../components/SuperBillReport";
import { useRoleController } from "../../../../../components/useRoleController";
import {
  STATE_LIST,
  STATE_RECORD_FULL_NAME_MAPPER,
} from "../../../../../constants";
import { AddSuperBillForm } from "../../../../../constants/provider/super-bill/add-super-bill/add-super-bill";
import { useGetProviderGroupProfile } from "../../../../../queries/admin/provider-group/use-get-provider-group-profile";
// import { useProviderListV2 } from "../../../../../queries/use-provider-list-v2";
import { useLocationsList } from "../../../../../queries/use-locationList";
import { Location } from "../../../../../services";
import { Identified } from "@/types/sdk-types";
import { DEF_PAGINATION, DGT_Pagination } from "../../../../../utils/utils";
import CardView from "../../patient/patient-list/patient-chart-new/assessment/summary/components/CardView";
import {
  isTelehealthPlaceOfService,
  useServiceLocationBilling,
} from "../use-service-location-billing";
import { placesOfService } from "./places-of-service";
import { getStoredRealm } from "@/utils/auth-storage";
interface ServiceDetailsProviderProps {
  formik: FormikProps<AddSuperBillForm>;
  viewBill: boolean;
  procedureCodeFormik?: any;
  // Form values enriched with the saved SuperBill's patient / provider details
  // for the printed report. Falls back to the raw form values when absent.
  reportData?: any;
  // A Patient Responsibility invoice presents the service details posted with
  // the claim it settles, so they are locked even on the editable form.
  isPatientResponsibilityInvoice?: boolean;
}

const ServiceDetailsProvider = ({
  formik,
  viewBill = true,
  procedureCodeFormik,
  reportData,
  isPatientResponsibilityInvoice = false,
}: ServiceDetailsProviderProps) => {
  // Service Location, Place Of Service, Date Of Service and Service State are
  // read-only on a view, and stay read-only on a PR invoice because they come
  // from the posted claim.
  const isServiceDetailLocked = !viewBill || isPatientResponsibilityInvoice;
  // const [searchProviderKey, setSearchProviderKey] = useState<string>("");
  const [searchProviderGroupKey, setSearchProviderGroupKey] =
    useState<string>("");
  const [locationPagination, setLocationPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { values, setFieldValue, errors, touched } = formik;
  const xTENANTID = getStoredRealm() ?? "";

  const { data: providerGroupLocationList, isFetching: isFetchingLocations } =
    useLocationsList({
      pagination: locationPagination,
      searchKey: searchProviderGroupKey,
      status: true,
      providerGroupId: xTENANTID,
    });

  // A picked location can live on a page that later scrolled out of
  // `content`, so keep every loaded location by uuid to resolve the selection.
  const locationByUuid = useRef<Record<string, Identified<Location>>>({});
  useEffect(() => {
    (providerGroupLocationList?.data?.content ?? []).forEach((it) => {
      if (it?.uuid) locationByUuid.current[it.uuid] = it;
    });
  }, [providerGroupLocationList?.data?.content]);

  // const { data: providersList } = useProviderListV2({
  //   status: true,
  //   pagination: {
  //     page: 0,
  //     pageSize: 20,
  //   },
  //   searchString: searchProviderKey,
  //   xTENANTID: getStoredRealm() ?? "",
  // });

  const handlePrintReport = () => {
    pdfRef.current?.generatePdf();
  };

  const pdfRef = useRef<{ generatePdf: () => void }>(null);

  const { data: providerSidePgProfileData }: any = useGetProviderGroupProfile(
    getStoredRealm() ?? undefined,
    viewBill
  );
  const { hasView: hasViewPrintSuperbill } =
    useRoleController("Print Superbill");

  // ECRH-5370: Service Location is optional for Telehealth encounters.
  const isTelehealth = isTelehealthPlaceOfService(values.placeOfService);
  const selectedLocationUuid = Object.keys(values.locationId ?? {})?.[0];

  // Resolve the Billing Address (and Service State) from the selected Service
  // Location, or from the Provider Group Profile when none is selected.
  const {
    serviceState: resolvedServiceState,
    serviceLocationAddress: resolvedBillingAddress,
    isResolved: isBillingResolved,
  } = useServiceLocationBilling(selectedLocationUuid);

  // Track the previously-resolved location so we can tell a real Service
  // Location change/removal apart from a form reinitialize (prefill) or a manual
  // Service State edit.
  const prevLocationRef = useRef<string | undefined>(undefined);

  // Keep Service State + the Billing Address in sync with the resolved source,
  // only while the form is editable (viewBill) so we never mutate a read-only
  // view:
  //  - When a Service Location is selected, it always drives the Service State
  //    from that Location's Billing Address.
  //  - When no Service Location is selected, default the Service State from the
  //    Provider Group (Billing Provider) state whenever the location was just
  //    cleared, or on initial load / prefill while it is still empty — so it
  //    auto-populates without any user interaction and survives the form's
  //    reinitialize. A manual dropdown selection is otherwise preserved.
  useEffect(() => {
    if (!viewBill || !isBillingResolved) return;

    const locationChanged = prevLocationRef.current !== selectedLocationUuid;
    prevLocationRef.current = selectedLocationUuid;

    const stateDiffers =
      (values.serviceState ?? "") !== (resolvedServiceState ?? "");

    if (selectedLocationUuid) {
      // Location selected -> it drives the Service State, but only when that
      // Location actually provides one. If the Location has no billing state
      // configured, never wipe an existing/prefilled value or a manual
      // selection with an empty string -> let the user pick one manually.
      if (resolvedServiceState && stateDiffers)
        setFieldValue("serviceState", resolvedServiceState);
    } else if ((locationChanged || !values.serviceState) && stateDiffers) {
      // No location -> default from the Provider Group state.
      setFieldValue("serviceState", resolvedServiceState ?? "");
    }
    setFieldValue("serviceLocationAddress", resolvedBillingAddress ?? null);
  }, [
    viewBill,
    isBillingResolved,
    resolvedServiceState,
    resolvedBillingAddress,
    selectedLocationUuid,
    values.serviceState,
  ]);

  return (
    <Box id={"Provider Information"}>
      <CardView
        title="Service Details"
        endAction={
          hasViewPrintSuperbill &&
          !viewBill && (
            <Button
              variant="contained"
              onClick={handlePrintReport}
              color="primary"
            >
              Print Report
            </Button>
          )
        }
        titleIcon={
          <MedicalServicesOutlinedIcon
            sx={{ color: "primary.main" }}
            fontSize="small"
          />
        }
      >
        {/* <Flex justifyContent={"space-between"} alignItems={"center"}> */}
        {/* <CustomTitle mb={1}>Service Details</CustomTitle> */}
        {/* </Flex> */}

        <ReportViewer pdfRef={pdfRef}>
          <SuperBillReport
            providerGroupData={providerSidePgProfileData}
            formData={(reportData ?? formik.values) as any}
            procedureCodes={procedureCodeFormik?.values}
          />
        </ReportViewer>

        <Grid container spacing={2} direction={"column"} p={0.8}>
          <Grid item container spacing={2}>
            <Grid item xs={3}>
              <CustomSelectScroll
                required={!isTelehealth}
                name="locationId"
                label="Service Location"
                readOnly={isServiceDetailLocked}
                placeholder="Select Service Location"
                loading={isFetchingLocations}
                pagination={locationPagination}
                setPagination={setLocationPagination}
                totalElements={
                  providerGroupLocationList?.data?.page?.totalElements
                }
                options={providerGroupLocationList?.data?.content?.map(
                  (it) => ({
                    label: it.name,
                    value: it.uuid,
                  })
                )}
                onSearch={(_, val) => {
                  setSearchProviderGroupKey(val);
                  setLocationPagination((p) => ({ ...p, page: 0 }));
                }}
                value={Object.keys(values.locationId ?? {})?.[0] ?? undefined}
                onChange={(value: string | null) => {
                  const curOption =
                    locationByUuid.current[value as string] ??
                    providerGroupLocationList?.data?.content?.find(
                      (it) => it.uuid === value
                    );
                  setFieldValue(
                    "locationId",
                    curOption
                      ? {
                          [curOption.uuid]: curOption.name,
                        }
                      : undefined
                  );
                }}
                error={touched?.locationId && errors.locationId}
                handleBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={3}>
              <CustomSelect
                readOnly={isServiceDetailLocked}
                required
                name="placeOfService"
                label="Place Of Service"
                options={placesOfService}
                value={values.placeOfService}
                onChange={(value) => {
                  setFieldValue("placeOfService", value);
                }}
                error={touched?.placeOfService && errors.placeOfService}
                handleBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={3}>
              <CustomDatePicker
                required
                name="dateOfService"
                disabled={isServiceDetailLocked}
                maxDate={dayjs(new Date())}
                label="Date Of Service"
                value={
                  values.dateOfService ? dayjs(values.dateOfService) : null
                }
                onChange={(value) => setFieldValue("dateOfService", value)}
                placeholder="Date Of Service"
                error={touched?.dateOfService && errors.dateOfService}
                handleBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={3}>
              {!viewBill ? (
                <Input
                  label="Service State"
                  textFieldProps={{
                    disabled: !viewBill,
                    placeholder: "Service State",
                    value: values?.serviceState
                      ? STATE_RECORD_FULL_NAME_MAPPER[values?.serviceState]
                      : "",
                  }}
                />
              ) : (
                <CustomSelect
                  required
                  readOnly={isServiceDetailLocked}
                  label="Service State"
                  name="serviceState"
                  options={STATE_LIST}
                  value={values.serviceState}
                  onChange={(option) => {
                    formik.setFieldValue("serviceState", option);
                  }}
                  error={touched?.serviceState && errors.serviceState}
                  handleBlur={formik.handleBlur}
                />
              )}
            </Grid>
            {/* <Grid item xs={3}>
              <CustomSelect
                readOnly={!viewBill}
                required
                name="renderingproviderId"
                label="Rendering Provider"
                placeholder="Select Rendering Provider"
                options={providersList?.data?.content?.map((it) => ({
                  value: it?.uuid,
                  label: it?.name,
                }))}
                onSearch={(_, val) => setSearchProviderKey(val)}
                value={
                  Object.keys(values.renderingproviderId ?? {})?.[0] ??
                  undefined
                }
                onChange={(value: string | null) => {
                  const curOption = providersList?.data?.content?.find(
                    (it) => it.uuid === value
                  );
                  setFieldValue(
                    "renderingproviderId",
                    curOption
                      ? {
                          [curOption.uuid]: curOption?.name,
                        }
                      : undefined
                  );
                }}
                error={
                  touched?.renderingproviderId && errors.renderingproviderId
                }
                handleBlur={formik.handleBlur}
              />
            </Grid> */}
            {/* ECRH-5336 */}
            {/* eCarehealth Production -10th july Tracker */}
            {/* Prior Authorization No. field removed from the invoice form */}
            {/* <Grid item xs={3}>
              <Input
                label="Prior Authorization"
                textFieldProps={{
                  disabled: !viewBill,
                  placeholder: "Prior Authorization",
                  value: values?.priorAuthorization,
                  onChange: (e) =>
                    formik.setFieldValue("priorAuthorization", e.target.value),
                }}
              />
            </Grid> */}
          </Grid>
        </Grid>
      </CardView>
    </Box>
  );
};

export default ServiceDetailsProvider;
